import type {
  AnalysisFailure,
  AnalysisFunctionRow,
  AnalysisSettings,
  AnalysisStyleNote,
  NoyauAnalysis,
  NoyauInstrumentFingerprint,
} from '../schemas/analysis.js';
import type { NoyauCode, NoyauDraw, NoyauScore } from '../schemas/noyau.js';
import { analyzeNoyaux, noyauScoresOf } from './analyst-notes.js';
import type { LevelReading } from './levels.js';
import { rankAnalyzedNoyaux } from './ranking.js';

/**
 * THE ANALYSIS, assembled (analyste.md §10, issue #196): one pure function from
 * what the two passes and the code produced to what a determination stores - the
 * six notes with their sentences, the principal, the second, the tie a draw
 * settled, and the analysis itself.
 *
 * The engine runs the steps in this order, and each is one function of `shared`:
 *
 * 1. the first pass answers, `readFunctionsAnswer` reads it, `functionRowsOf`
 *    pairs it with what was served;
 * 2. `readLevels` fixes, arbitrates, eliminates and computes the compatibility -
 *    and names the candidates the second pass is asked about;
 * 3. the second pass answers, `readStyleAnswer` reads it, `verifiedStyleNotes`
 *    checks its passages - or it fails twice and its style is `absent`;
 * 4. `settleNoyauAnalysis` notes the six, ranks them, and assembles the analysis.
 *
 * Writing it whole in ONE place is what keeps the invariants by construction: six
 * rows in the order of the table, three levels in the order of a triplet, a
 * principal that is a candidate, a second that is the next one, eliminated or not.
 */

/** What a determination stores once it is settled `rendered` (§10). */
export type SettledNoyauAnalysis = {
  readonly analysis: NoyauAnalysis;
  /** The six notes, in the order of the table, each with its sentence. */
  readonly scores: NoyauScore[];
  readonly principal: NoyauCode;
  readonly second: NoyauCode;
  /** The tie the draw settled, or null when the notes alone decided both places. */
  readonly draw: NoyauDraw | null;
};

/** The second pass as the analysis keeps it: its notes after the check of their passages, or its absence. */
export type SettledStyle =
  | { readonly state: 'rendered'; readonly notes: readonly AnalysisStyleNote[] }
  | { readonly state: 'absent' };

/**
 * SETTLE a determination (§8, §10): note the six noyaux from the compatibility and
 * the style - the candidates by the weight of the section, the eliminated capped
 * under the lowest candidate -, rank them with the draw it is handed, and assemble
 * the analysis.
 *
 * `style` is `absent` when the second pass failed twice (§7.5): the candidates
 * are noted on their compatibility alone, and the analysis says « style absent ».
 * `drawOrder` is the permutation of the six the server drew (N2), an ARGUMENT, so
 * a tie is pinned in a test without mocking a global.
 */
export function settleNoyauAnalysis(input: {
  readonly settings: AnalysisSettings;
  readonly instrument: NoyauInstrumentFingerprint | null;
  readonly functions: readonly AnalysisFunctionRow[];
  readonly reading: LevelReading;
  readonly style: SettledStyle;
  readonly drawOrder: readonly NoyauCode[];
}): SettledNoyauAnalysis {
  const { settings, style, reading } = input;
  const rows = analyzeNoyaux({
    reading,
    styles:
      style.state === 'rendered'
        ? style.notes.map((note) => ({
            noyau: note.noyau,
            score: note.score,
            justification: note.justification,
          }))
        : null,
    styleWeight: settings.styleWeight,
  });
  const ranking = rankAnalyzedNoyaux(
    rows.map((row) => ({ noyau: row.noyau, score: row.score, eliminated: !row.candidate })),
    input.drawOrder,
  );
  return {
    analysis: {
      settings,
      instrument: input.instrument,
      failure: null,
      functions: [...input.functions],
      levels: reading.levels,
      conflicts: reading.conflicts,
      noyaux: rows.map((row) => ({
        noyau: row.noyau,
        candidate: row.candidate,
        eliminatedBy: row.eliminatedBy,
        compatibility: row.compatibility,
        style: row.style,
      })),
      style:
        style.state === 'rendered'
          ? { state: 'rendered', notes: [...style.notes] }
          : { state: 'absent' },
    },
    scores: noyauScoresOf(rows),
    principal: ranking.principal,
    second: ranking.second,
    draw: ranking.draw,
  };
}

/**
 * An analysis with its HEADER ONLY - the settings, the instrument, a reason if
 * the determination was settled without a call (§9.2, G22) - and none of what the
 * passes would have put in it: a determination that is `failed`, `no-extracts` or
 * still pending under the analyst in two passes. Never null: a null analysis says
 * « lecture directe, avant #196 », and these were read by the new instrument.
 */
export function headerOnlyAnalysis(input: {
  readonly settings: AnalysisSettings;
  readonly instrument?: NoyauInstrumentFingerprint | null | undefined;
  readonly failure?: AnalysisFailure | null | undefined;
}): NoyauAnalysis {
  return {
    settings: input.settings,
    instrument: input.instrument ?? null,
    failure: input.failure ?? null,
    functions: [],
    levels: [],
    conflicts: [],
    noyaux: [],
    style: null,
  };
}

/**
 * The noyaux an analysis eliminated - none for a determination of before #196,
 * which has no analysis. What `sortedNoyauScores` takes to list them after every
 * candidate, and what `secondEliminatedOf` reads.
 */
export function eliminatedNoyaux(analysis: NoyauAnalysis | null): NoyauCode[] {
  return analysis === null
    ? []
    : analysis.noyaux.filter((row) => !row.candidate).map((row) => row.noyau);
}

/**
 * Whether the SECOND of a determination is an eliminated noyau (§8.5, G31): then
 * the principal is retained whatever the gap and whatever card is taken. False
 * without an analysis - a determination of before #196 has no eliminated noyau -
 * and without a second.
 */
export function secondEliminatedOf(
  analysis: NoyauAnalysis | null,
  second: NoyauCode | null,
): boolean {
  return second !== null && eliminatedNoyaux(analysis).includes(second);
}
